import React, { useState, useEffect } from 'react';

/**
 * 결제 기간 선택 Bottom Sheet
 * [NEW] 어둡고 모던한 테마에 맞춘 Bottom Sheet UI
 * @param {object} props
 * @param {boolean} props.isOpen - 모달 표시 여부
 * @param {function} props.onClose - 모달 닫기/취소 핸들러
 * @param {function} props.onApply - '적용하기' 버튼 클릭 시 핸들러
 * @param {number} props.currentMonthsToShow - 현재 선택된 기간 (프리셋 활성화 표시용)
 */

// 현재 날짜 (PaymentHistory와 동기화)
const TODAY = new Date('2025-11-07');

/**
 * Helper: 현재 기준으로부터 N개월 전/후의 년도와 월을 배열로 생성 (YYYY.MM 포맷)
 */
const getMonthOptions = (count = 24, today = TODAY) => {
    const options = [];
    const currentDate = new Date(today);
    currentDate.setDate(1); // 계산 안정화를 위해 월의 1일로 설정

    // 최대 count개월 전까지의 월을 생성
    for (let i = 0; i < count; i++) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1; // 1부터 시작
        const value = `${year}.${String(month).padStart(2, '0')}`;
        const label = `${year}년 ${month}월`;
        options.push({ value, label });

        // 다음 월로 이동
        currentDate.setMonth(currentDate.getMonth() - 1);

        // setMonth 후 날짜가 꼬이는 현상 방지 (월의 마지막 날짜로 이동)
        if (currentDate.getDate() !== 1) {
            currentDate.setDate(0);
        }
    }
    return options.reverse(); // 오래된 순(시작 날짜)으로 정렬
};


function PaymentPeriodBottomSheet({ isOpen, onClose, onApply, currentMonthsToShow }) {

    // 선택 가능한 모든 월 목록 (예: 2년간)
    const monthOptions = getMonthOptions(24, TODAY);
    const latestMonthValue = monthOptions[monthOptions.length - 1]?.value || '';

    // 내부 상태 1: 선택된 프리셋 개월 수 (3, 6, 12, Infinity 또는 null for custom)
    const [selectedMonths, setSelectedMonths] = useState(currentMonthsToShow || 3);

    // 내부 상태 2: 사용자 정의 시작/종료 월 (YYYY.MM 포맷)
    const [startMonth, setStartMonth] = useState(monthOptions[0]?.value || '');
    const [endMonth, setEndMonth] = useState(latestMonthValue);

    if (!isOpen) {
        return null;
    }

    // 프리셋 클릭 핸들러: 프리셋 선택 시 사용자 정의 월 선택 해제
    const handlePresetClick = (months) => {
        setSelectedMonths(months);
    };

    // [NEW] 사용자 정의 월 선택 핸들러: 월 선택 시 프리셋 선택 해제
    const handleMonthChange = (type, value) => {
        if (type === 'start') {
            setStartMonth(value);
        } else {
            setEndMonth(value);
        }
        setSelectedMonths(null); // 프리셋 선택 해제
    };


    // 적용 버튼 핸들러 (로직 수정)
    const handleApply = () => {
        let newMonthsToShow;

        if (selectedMonths !== null) {
            // 프리셋이 선택된 경우 (3, 6, 12, Infinity)
            newMonthsToShow = selectedMonths;

        } else {
            // [NEW] 사용자 정의 기간이 선택된 경우

            // YYYY.MM 문자열을 Date 객체로 파싱
            const [startY, startM] = startMonth.split('.').map(Number);
            const [endY, endM] = endMonth.split('.').map(Number);

            // 시작/종료 Date 객체 생성 (월은 0-index이므로 -1)
            const startDate = new Date(startY, startM - 1, 1);
            const endDate = new Date(endY, endM, 1); // 선택한 달의 다음 달 1일

            // 월 차이 계산: (종료 월) - (시작 월)
            // (연도 차이 * 12 + 월 차이)
            const diffMonths = (endDate.getFullYear() * 12 + endDate.getMonth()) - (startDate.getFullYear() * 12 + startDate.getMonth());

            newMonthsToShow = diffMonths;

            if (newMonthsToShow <= 0) {
                // 시작월이 종료월보다 같거나 늦다면 3개월 기본값 또는 오류 처리
                console.error("선택된 시작월이 종료월보다 늦습니다.");
                newMonthsToShow = 3;
            }
        }

        onApply({ newMonthsToShow });
    };

    return (
        // 모달 배경 (Overlay)
        <div style={periodModalStyles.periodModalPlaceholder} onClick={onClose}>
            {/* 모달 컨텐츠 (배경 클릭 시 닫히지 않도록 이벤트 전파 차단) */}
            <div style={periodModalStyles.periodModalContent} onClick={(e) => e.stopPropagation()}>

                {/* 1. 모달 헤더/제목 */}
                <div style={periodModalStyles.header}>
                    <h3 style={periodModalStyles.title}>기간 선택</h3>
                    <button style={periodModalStyles.closeButton} onClick={onClose}>
                        X
                    </button>
                </div>

                {/* 2. 프리셋 버튼 그룹 */}
                <div style={periodModalStyles.presetGroup}>
                    {[3, 6, 12, Infinity].map((months, index) => (
                        <button
                            key={index}
                            // 프리셋 버튼은 selectedMonths가 null이 아닐 때만 활성화
                            style={{
                                ...periodModalStyles.presetButton,
                                ...(months === selectedMonths ? periodModalStyles.presetActive : {})
                            }}
                            onClick={() => handlePresetClick(months)}
                        >
                            {months === Infinity ? '전체' : `최근 ${months}개월`}
                        </button>
                    ))}
                </div>

                {/* 3. 기간 선택 (년-월 Select Box) */}
                <div style={periodModalStyles.dateRangeSelectGroup}>
                    {/* 시작 날짜 선택 */}
                    <select
                        style={periodModalStyles.dateSelectBox}
                        value={startMonth}
                        onChange={(e) => handleMonthChange('start', e.target.value)}
                    >
                        {monthOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <span style={periodModalStyles.dateDivider}>~</span>

                    {/* 종료 날짜 선택 */}
                    <select
                        style={periodModalStyles.dateSelectBox}
                        value={endMonth}
                        onChange={(e) => handleMonthChange('end', e.target.value)}
                    >
                        {monthOptions.map(option => (
                            <option
                                key={option.value}
                                value={option.value}
                                // 시작월보다 이전의 월은 비활성화
                                disabled={option.value < startMonth}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 4. 적용하기 버튼 (액션 버튼) */}
                <button style={periodModalStyles.applyButton} onClick={handleApply}>
                    적용하기
                </button>
            </div>
        </div>
    );
}


// [NEW] 단독 컴포넌트에 포함된 스타일 (Bottom Sheet 테마)
const periodModalStyles = {
    // 오버레이 및 컨텐츠 레이아웃
    periodModalPlaceholder: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
        zIndex: 1001, backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    periodModalContent: {
        background: '#1B1931', // 다크 배경
        padding: '24px 24px 24px 24px',
        borderRadius: '16px 16px 0 0',
        width: '100%',
        boxSizing: 'border-box',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
    },

    // 헤더 및 버튼 스타일
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        width: '100%', marginBottom: '24px',
    },
    title: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '18px', color: '#FCFCFA', margin: 0,
    },
    closeButton: {
        background: 'none', border: 'none', color: '#9A9A9A', fontSize: '18px', cursor: 'pointer',
    },
    presetGroup: {
        display: 'flex', justifyContent: 'space-between', gap: '8px', width: '100%', marginBottom: '24px',
    },
    presetButton: {
        flex: 1, padding: '10px 0', borderRadius: '6px', cursor: 'pointer',
        fontFamily: 'Pretendard', fontSize: '14px', fontWeight: '500',
        background: '#3A394A', // 비활성화 배경
        color: '#FCFCFA', // 비활성화 텍스트
        border: 'none',
    },
    presetActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)', // 활성화 배경 (테마 악센트)
    },

    // [NEW] 년-월 선택 Select Group 스타일
    dateRangeSelectGroup: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
        padding: '10px 0',
        marginBottom: '40px',
        gap: '10px',
    },
    dateSelectBox: {
        flex: 1,
        padding: '10px 15px',
        borderRadius: '6px',
        fontFamily: 'Pretendard',
        fontWeight: '600',
        fontSize: '16px',
        color: '#FCFCFA',
        backgroundColor: '#3A394A', // 배경색 변경
        border: '1px solid #555',
        appearance: 'none', // 기본 드롭다운 스타일 제거
        cursor: 'pointer',
        textAlign: 'center',
    },
    dateDivider: {
        color: '#9A9A9A', margin: '0 5px', fontSize: '16px',
    },

    applyButton: {
        width: '100%', padding: '16px', borderRadius: '8px', border: 'none',
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '16px', color: '#FFFFFF',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)', // 테마 악센트 버튼
        cursor: 'pointer',
    },
};

export default PaymentPeriodBottomSheet;